import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import {
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  HelpOutlineRounded,
  InfoOutlineRounded,
  TimelineRounded,
  NavigateNext,
  AddRounded,
  AlignHorizontalCenterRounded,
} from '@mui/icons-material'
import { isEmpty } from 'lodash'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  ContextModel,
  GapCause,
  GapExtended,
  Module as ModuleType,
} from '../../shared/model/models'
import FsGapDescription from '../../components/fs-gap-detail/fs-gap-detail'
import { diversityDimensionMap, moduleConfigMap, WALKTHROUGH_STATUS } from '../../shared/constants'
import { ModuleConfig } from '../../shared/model'
import FsOptionsForActions from '../../components/fs-options-for-actions/fs-options-for-actions'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  setContextVariantGapLatest,
  changeWalkthroughIndex,
  changeWalkthroughStatus,
  setContextVariantGaps,
} from '../../store/actions'
import { translatedDomainName } from '../../shared/utils'
import { getIcon } from '../../components/common/Icons'
import FsFullPageLoader from '../../components/common/atoms/FullPageLoader'
import FsLeftInfoDrawer from '../../components/common/atoms/LeftInfoDrawer'
import AnimatedProgressBar from '../../components/common/molecules/AnimatedProgressBar'

type NavigationParams = {
  id?: string
}

const FairView = () => {
  const { t, i18n } = useTranslation()
  const { id } = useParams<NavigationParams>()
  const contextModelVariantId = parseInt(id!, 10)
  const [latestGap, setLatestGap] = useState<GapExtended | undefined>(undefined)
  const [moduleConfig, setModuleConfig] = useState<ModuleConfig>()
  const [contextModels, setContextModels] = useState<ContextModel[]>([])
  const {
    modules,
    contextVariantGapLatest,
    currentRun,
    runs,
    contextVariantGaps,
    ofaValues,
    ofaOverall,
    cwiMappings,
  } = useAppSelector((state) => state.currentAnalysis)
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { walkthroughStatus, walkthroughIndex, currentLanguage } = useAppSelector(
    (state) => state.currentUser,
  )
  const [isCauseHovered, setIsCauseHovered] = useState<boolean>(false)
  const [lineProps, setLineProps] = useState<any>(null)

  const findCurrentModule = (odules: ModuleType[]) =>
    odules.find((module) =>
      module.contextModels.some((cm) =>
        cm.contextModelVariants.some((cmv) => cmv.id === contextModelVariantId),
      ),
    )

  const findCurrentContextModel = () =>
    contextModels?.find((cm) =>
      cm.contextModelVariants.some((cmv) => cmv.id === contextModelVariantId),
    )

  useEffect(() => {
    if (modules) {
      const currentModule = findCurrentModule(modules)
      if (currentModule) {
        setModuleConfig(moduleConfigMap.get(currentModule.name))
        setContextModels(currentModule.contextModels)
      }
    }
  }, [id, modules])

  useEffect(() => {
    if (currentRun) {
      const cmvId = parseInt(id!, 10)
      if (contextVariantGapLatest && !isEmpty(contextVariantGapLatest[cmvId])) {
        setLatestGap(contextVariantGapLatest[cmvId])
      } else {
        dispatch(setContextVariantGapLatest({ contextVariantId: cmvId, date: currentRun.runAt }))
      }
    }
  }, [id, contextVariantGapLatest, currentRun])

  const curDivDimName = findCurrentContextModel()?.diversityDimension.name ?? ''
  const diversityDimenstionConfig = diversityDimensionMap.get(curDivDimName)

  useEffect(() => {
    if (ofaValues && walkthroughStatus === WALKTHROUGH_STATUS.Paused && walkthroughIndex === 3) {
      dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.InProgress))
      dispatch(changeWalkthroughIndex(4))
    }
  }, [walkthroughStatus, walkthroughIndex, ofaValues])

  let posGapValue = 0
  let neGapValue = 0

  if (latestGap) {
    const gapCauses = [...latestGap.causes].sort(
      (a, b) => Number(b.explainedPart) - Number(a.explainedPart),
    )
    gapCauses.forEach((cause: GapCause) => {
      /* exclude constant effect (by convention name=constant) for explained part only, because its by convention always zero */
      if (cause.empiricalConcept.name !== 'constant') {
        if (Math.round((Number(cause.explainedPart) + Number.EPSILON) * 1000) / 1000 >= 0) {
          posGapValue += Math.round((Number(cause.explainedPart) + Number.EPSILON) * 1000) / 1000
        }
      }
    })
  }

  posGapValue = Math.round((Number(posGapValue) + Number.EPSILON) * 100) / 100

  const [helpOpen, setHelpOpen] = useState(false)
  const handleHelpOpen = () => setHelpOpen(true)
  const handleHelpClose = () => setHelpOpen(false)

  const navigate = useNavigate()

  const unadjusted = Math.round((Number(latestGap?.unadjusted ?? 0) + Number.EPSILON) * 100) / 100
  const adjusted = Math.round((Number(latestGap?.adjusted ?? 0) + Number.EPSILON) * 100) / 100
  const explained = Math.round((Number(unadjusted - adjusted) + Number.EPSILON) * 100) / 100
  neGapValue = Math.round((Number(explained - posGapValue) + Number.EPSILON) * 100) / 100
  const totalHeight = 300
  const totalVisualHeight = Math.abs(adjusted) + Math.abs(explained)
  const explainedHeight = (Math.abs(explained) / totalVisualHeight) * totalHeight
  const unexplainedHeight = totalHeight - explainedHeight
  const totalGapVisualHeight = Math.abs(posGapValue) + Math.abs(neGapValue)
  const posGapHeight = (Math.abs(posGapValue) / totalGapVisualHeight) * totalHeight
  const negGapHeight = totalHeight - posGapHeight

  const helpTextHtml =
    latestGap && latestGap.gapResultDescriptions.filter((desc) => desc.locale === i18n.language)
      ? latestGap.gapResultDescriptions.filter((desc) => desc.locale === i18n.language)[0]
          .gapDescription
      : ''

  useEffect(() => {
    if (runs) {
      const firstRun = runs[0]
      const lastRun = runs[runs.length - 1]

      if (!contextVariantGaps || isEmpty(contextVariantGaps[contextModelVariantId])) {
        dispatch(
          setContextVariantGaps({
            contextVariantId: contextModelVariantId,
            startDate: new Date(firstRun.runAt),
            endDate: new Date(lastRun.runAt),
          }),
        )
      }
    }
  }, [contextModelVariantId, runs, contextVariantGaps])

  const sortedGaps =
    contextVariantGaps && contextVariantGaps[contextModelVariantId]
      ? [...contextVariantGaps[contextModelVariantId]].sort((a, b) => a.id - b.id)
      : []
  const prevIndex =
    latestGap && sortedGaps.length > 1 ? sortedGaps.findIndex((g) => g.id === latestGap.id) - 1 : -1
  const unadjDiff =
    prevIndex !== -1 && sortedGaps && sortedGaps[prevIndex]
      ? Math.round((unadjusted + Number.EPSILON) * 100) / 100 -
        Math.round((Number(sortedGaps[prevIndex].unadjusted ?? 0) + Number.EPSILON) * 100) / 100
      : 0
  const adjDiff =
    prevIndex !== -1 && sortedGaps && sortedGaps[prevIndex]
      ? Math.round((adjusted + Number.EPSILON) * 100) / 100 -
        Math.round((Number(sortedGaps[prevIndex].adjusted ?? 0) + Number.EPSILON) * 100) / 100
      : 0

  const updateLine = () => {
    const el1 = document.getElementById('hovered-class-1')
    const el2 = document.getElementById('hovered-class-2')

    if (!el1 || !el2) return

    const rect1 = el1.getBoundingClientRect()
    const rect2 = el2.getBoundingClientRect()

    const x1 = rect1.right
    const y1 = rect1.top + rect1.height / 2
    const x2 = rect2.left
    const y2 = rect2.top + rect2.height / 2

    const dx = x2 - x1
    const dy = y2 - y1

    // Control points: play with these values for tighter or looser S
    const cx1 = x1 + dx * 0.5
    const cy1 = y1

    const cx2 = x2 - dx * 0.5
    const cy2 = y2

    const d = `M ${x1},${y1} C ${cx1},${cy1} ${cx2},${cy2} ${x2},${y2}`

    setLineProps(d)
  }

  useEffect(() => {
    if (isCauseHovered) {
      updateLine()
    } else {
      setLineProps(null)
    }
  }, [isCauseHovered])

  return (
    <>
      {contextModels &&
        id &&
        moduleConfig &&
        diversityDimenstionConfig &&
        contextVariantGaps &&
        contextVariantGaps[contextModelVariantId] && (
          <>
            <Box>
              <Stack direction="row" alignItems="center">
                <Box mr={1} mt={0.75}>
                  {getIcon(moduleConfig?.module, {
                    color:
                      moduleConfig?.fsColor[theme.palette.mode === 'light' ? 'primary' : 'light'],
                  })}
                </Box>
                {getIcon(diversityDimenstionConfig?.diversityDimension, {
                  color:
                    diversityDimenstionConfig?.fsColor[
                      theme.palette.mode === 'light' ? 'primary' : 'light'
                    ],
                })}
                <Typography variant="h1" ml={1} component="div" className="flex items-end">
                  {t(`module.name.${moduleConfig?.module}`)} {t('by')}{' '}
                  {t(`context.model.name.${curDivDimName}` as any)}
                  <Typography variant="h3" ml={1} mb={0.35}>
                    (
                    {translatedDomainName(
                      latestGap?.description.groupOfInterest.groupVariable.label ?? '',
                      t,
                    )}{' '}
                    {t('vs.')}{' '}
                    {translatedDomainName(
                      latestGap?.description.referenceGroup.groupVariable.label ?? '',
                      t,
                    )}
                    )
                  </Typography>
                </Typography>
                <Box
                  pl={1}
                  pt={1}
                  onClick={handleHelpOpen}
                  sx={{ cursor: 'pointer', color: theme.palette.text.secondary }}
                >
                  <InfoOutlineRounded sx={{ fontSize: 26 }} />
                </Box>
              </Stack>
            </Box>

            <Stack
              mb={2}
              direction={{ sm: 'column', md: 'row' }}
              alignItems={{ sm: 'flex-start', md: 'center' }}
              justifyContent={{ sm: 'center', md: 'space-between' }}
            >
              <Stack direction="row" alignItems="center" mt={1.5}>
                <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
                  <Link
                    underline="hover"
                    key="1"
                    color="text.secondary"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/fs/dashboard')}
                  >
                    <Typography variant="body2">{t('overview')}</Typography>
                  </Link>
                  <Typography color="text.primary" variant="body2" fontWeight="bold">
                    {t(`module.name.${moduleConfig?.module}`)} {t('by')}{' '}
                    {t(`context.model.name.${curDivDimName}` as any)}
                  </Typography>
                </Breadcrumbs>
              </Stack>
              <Button
                variant="outlined"
                sx={{ mt: 2, mb: -2 }}
                onClick={() => {
                  if (walkthroughStatus === WALKTHROUGH_STATUS.InProgress) {
                    dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Paused))
                  }
                  navigate('/fs/reports')
                }}
                startIcon={getIcon('reports')}
                className="reporticon"
              >
                <Typography>{t('get.report')}</Typography>
              </Button>
            </Stack>
            <Box pb={4} sx={{ overflowY: 'auto', height: 'calc(100vh - 173px)' }}>
              <Grid container spacing={2} p={1}>
                <Grid size={{ xs: 12, md: 12, lg: 3 }} className="flex">
                  <Card
                    elevation={1}
                    sx={{
                      boxShadow: `2px 2px 8px 2px ${moduleConfig.fsColor.primary}66`,
                      // border: `1px solid ${moduleConfig.fsColor.primary}66`,
                      borderRadius: 3,
                    }}
                    className="w-full"
                  >
                    <CardContent>
                      {latestGap &&
                        contextVariantGaps &&
                        contextVariantGaps[contextModelVariantId] && (
                          <FsGapDescription
                            latestGap={latestGap}
                            moduleConfig={moduleConfig}
                            diversityDimenstionConfig={diversityDimenstionConfig}
                            prevGap={contextVariantGaps[contextModelVariantId]}
                          />
                        )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 12, lg: 9 }} container spacing={2} className="flex">
                  <Grid size={{ xs: 12, md: 6, lg: 5.5 }} className="flex">
                    <Card
                      elevation={1}
                      sx={{
                        boxShadow: `2px 2px 8px 2px ${moduleConfig.fsColor.primary}66`,
                        // border: `1px solid ${moduleConfig.fsColor.primary}66`,
                        borderRadius: 3,
                      }}
                      className="w-full gaps_walkthrough"
                    >
                      <CardContent className="h-full">
                        <Stack direction="row" justifyContent="flex-end" alignItems="flex-start">
                          <Tooltip
                            slotProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: 1000,
                                  borderRadius: 2,
                                },
                              },
                            }}
                            title={
                              <Grid
                                container
                                spacing={4}
                                className="px-2 py-4 flex-row items-center justify-center"
                              >
                                <Grid size={{ xs: 8 }}>
                                  <Typography variant="h3" mt={2}>
                                    {t('detailed.desc.status.title')}
                                  </Typography>
                                  <Typography my={2} component="div">
                                    {t('the')}{' '}
                                    <Typography className="font-semibold" display="inline">
                                      {t('unadjustede')} {t(`${moduleConfig?.module}_gap`)}
                                    </Typography>{' '}
                                    {t('detailed.desc.status.section1', {
                                      helptext: t(
                                        `dependentCharacteristic.helptext.${latestGap?.dependentCharacteristic.name}` as any,
                                      ),
                                    })}
                                  </Typography>
                                  <Typography mb={2} component="div">
                                    {t('the')}{' '}
                                    <Typography className="font-semibold" display="inline">
                                      {t('adjustede')} {t(`${moduleConfig?.module}_gap`)}
                                    </Typography>{' '}
                                    {t('detailed.desc.status.section2', {
                                      divdim: t(
                                        `context.model.name.${diversityDimenstionConfig?.diversityDimension}`,
                                      ),
                                    })}
                                  </Typography>
                                  <Typography variant="body2" className="font-semibold">
                                    {t('detailed.desc.causes.graph.illus')}
                                  </Typography>
                                </Grid>
                                <Grid size={{ xs: 4 }} className="py-4 flex flex-row items-center">
                                  <Typography mr={0.75}>
                                    {t('unadjusteder')} {t(`${moduleConfig?.module}_gap`)}
                                  </Typography>
                                  <Box
                                    width={40}
                                    height={2}
                                    sx={{ backgroundColor: theme.palette.text.secondary }}
                                  />
                                  <Box
                                    sx={{
                                      width: '6px',
                                      height: totalHeight + 32,
                                      border: `2px solid ${theme.palette.text.secondary}`,
                                      borderRight: 0,
                                      borderRadius: 1,
                                      borderTopRightRadius: 0,
                                      borderBottomRightRadius: 0,
                                    }}
                                  />

                                  <Box className="flex flex-col md:flex-row items-center justify-center p-4 pr-0">
                                    <Box
                                      className={`relative h-[${
                                        totalHeight + 32
                                      }px] w-6 rounded-lg`}
                                    >
                                      <Box
                                        className="w-6 rounded-lg"
                                        style={{
                                          height: '200px',
                                          backgroundColor: theme.palette.divider,
                                        }}
                                      />
                                      <Box className="w-4 rounded-full h-4 my-2 mx-1 flex items-center justify-center">
                                        <AddRounded sx={{ fontSize: 16 }} />
                                      </Box>
                                      <Box
                                        className="w-6 rounded-lg"
                                        style={{
                                          height: '100px',
                                          backgroundColor:
                                            diversityDimenstionConfig?.fsColor.primary,
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                  <Stack>
                                    <Stack height={200} className="justify-center mb-4">
                                      <Stack className="flex-row items-center">
                                        <Box
                                          width={40}
                                          height={2}
                                          sx={{ backgroundColor: theme.palette.divider }}
                                        />
                                        <Typography ml={1}>
                                          {t('adjusteder')} {t(`${moduleConfig?.module}_gap`)}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                    <Stack height={100} className="justify-center mt-4">
                                      <Stack className="flex-row items-center">
                                        <Box
                                          width={40}
                                          height={2}
                                          sx={{
                                            backgroundColor:
                                              diversityDimenstionConfig?.fsColor.primary,
                                          }}
                                        />
                                        <Typography ml={1}>
                                          {t('explained')} {t(`${moduleConfig?.module}_gap`)}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                  </Stack>
                                </Grid>
                              </Grid>
                            }
                            placement="bottom"
                            arrow
                            enterTouchDelay={0}
                          >
                            <InfoOutlineRounded
                              fontSize="small"
                              sx={{ ml: 1, mb: -4, color: theme.palette.text.secondary }}
                            />
                          </Tooltip>
                        </Stack>
                        <Typography variant="h2" className="font-semibold" mt={-0.5}>
                          {t(`${moduleConfig?.module}_gap`)}
                        </Typography>

                        {latestGap?.adjusted !== null ? (
                          <Stack className="pt-4 pb-12 flex flex-row items-center justify-center mt-4">
                            <Tooltip
                              placement="top"
                              arrow
                              enterTouchDelay={0}
                              title={
                                <Typography>
                                  {t(
                                    moduleConfig?.module === 'pay'
                                      ? 'explain.unadjusted.pay.gap'
                                      : 'explain.unadjusted.gap',
                                    {
                                      gap: t(`${moduleConfig?.module}_gap`),
                                      unit: t(
                                        `dependentCharacteristic.name.${latestGap?.dependentCharacteristic.name}` as any,
                                      ),
                                    },
                                  )}
                                </Typography>
                              }
                            >
                              <Stack className="mr-4 min-w-[125px] max-w-[190px] text-start">
                                <Stack className="text-start flex-row">
                                  <Typography variant="n2" mr={1}>{`${Number(
                                    unadjusted,
                                  ).toLocaleString(currentLanguage, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })} %`}</Typography>
                                  <HelpOutlineRounded
                                    fontSize="small"
                                    sx={{ color: theme.palette.text.secondary }}
                                  />
                                </Stack>
                                <Typography>{t('unadjusteder')}</Typography>
                                <Typography>{t(`${moduleConfig?.module}_gap`)}</Typography>
                              </Stack>
                            </Tooltip>

                            <Box
                              width={40}
                              height={2}
                              sx={{ backgroundColor: theme.palette.text.secondary }}
                            />
                            <Box
                              sx={{
                                width: '6px',
                                height: totalHeight + 32,
                                border: `2px solid ${theme.palette.text.secondary}`,
                                borderRight: 0,
                                borderRadius: 1,
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                              }}
                            />
                            <Box className="flex flex-col md:flex-row items-center justify-center p-4 pr-0">
                              <Box className={`relative h-[${totalHeight + 32}px] w-6 rounded-lg`}>
                                <AnimatedProgressBar
                                  progress={unexplainedHeight}
                                  color={theme.palette.divider}
                                  orientation="vertical"
                                />
                                <Box className="w-4 rounded-full h-4 my-2 mx-1 flex items-center justify-center">
                                  <AddRounded sx={{ fontSize: 16 }} />
                                </Box>
                                <AnimatedProgressBar
                                  progress={explainedHeight}
                                  color={diversityDimenstionConfig?.fsColor.primary}
                                  orientation="vertical"
                                />
                              </Box>
                            </Box>
                            <Stack>
                              <Stack height={unexplainedHeight} className="justify-center mb-4">
                                <Stack className="flex-row items-center">
                                  <Box
                                    width={40}
                                    height={2}
                                    sx={{ backgroundColor: theme.palette.divider }}
                                  />
                                  <Tooltip
                                    placement="top"
                                    arrow
                                    enterTouchDelay={0}
                                    title={
                                      <Typography>
                                        {t('explain.adjusted.gap', {
                                          gap: t(`${moduleConfig?.module}_gap`),
                                        })}
                                      </Typography>
                                    }
                                  >
                                    <Stack className="ml-4 min-w-[135px] max-w-[190px]">
                                      <Stack className="text-start flex-row">
                                        <Typography variant="n2" mr={1}>{`${Number(
                                          adjusted,
                                        ).toLocaleString(currentLanguage, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })} %`}</Typography>
                                        <HelpOutlineRounded
                                          fontSize="small"
                                          sx={{ color: theme.palette.text.secondary }}
                                        />
                                      </Stack>
                                      <Typography>{t('adjusteder')}</Typography>
                                      <Typography>{t(`${moduleConfig?.module}_gap`)}</Typography>
                                    </Stack>
                                  </Tooltip>
                                </Stack>
                              </Stack>
                              <Stack height={explainedHeight} className="justify-center mt-4">
                                <Stack className="flex-row items-center">
                                  <Box
                                    width={40}
                                    height={2}
                                    sx={{
                                      backgroundColor: diversityDimenstionConfig?.fsColor.primary,
                                    }}
                                  />

                                  <Tooltip
                                    placement="bottom"
                                    arrow
                                    enterTouchDelay={0}
                                    title={
                                      <Typography>
                                        {t('explain.explained.gap', {
                                          gap: t(`${moduleConfig?.module}_gap`),
                                        })}
                                      </Typography>
                                    }
                                  >
                                    <Box
                                      id="hovered-class-1"
                                      className="rounded-xl py-2 px-2 min-w-[135px] max-w-[190px]"
                                      sx={{
                                        backgroundColor: isCauseHovered
                                          ? `${diversityDimenstionConfig?.fsColor.primary}33`
                                          : '',
                                        outline: isCauseHovered ? `1px solid` : '',
                                        outlineColor: isCauseHovered
                                          ? `${diversityDimenstionConfig?.fsColor.primary}90`
                                          : '',
                                      }}
                                      onMouseEnter={() => setIsCauseHovered(true)}
                                      onMouseLeave={() => setIsCauseHovered(false)}
                                    >
                                      <Stack className="ml-4">
                                        <Stack className="text-start flex-row">
                                          <Typography variant="n2" mr={1}>{`${Number(
                                            explained,
                                          ).toLocaleString(currentLanguage, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })} %`}</Typography>
                                          <HelpOutlineRounded
                                            fontSize="small"
                                            sx={{ color: theme.palette.text.secondary }}
                                          />
                                        </Stack>
                                        <Typography>{t('explained')}</Typography>
                                        <Typography>{t(`${moduleConfig?.module}_gap`)}</Typography>
                                      </Stack>
                                    </Box>
                                  </Tooltip>
                                </Stack>
                              </Stack>
                            </Stack>
                          </Stack>
                        ) : (
                          <Box className="mt-4">
                            <Grid
                              container
                              spacing={1}
                              mt={1}
                              display="flex"
                              justifyContent="flex-start"
                            >
                              <Grid size={{ xs: 6 }} className="flex pl-2 pt-2">
                                <Tooltip
                                  placement="right"
                                  arrow
                                  enterTouchDelay={0}
                                  title={
                                    <Typography>
                                      {t(
                                        moduleConfig?.module === 'pay'
                                          ? 'explain.unadjusted.pay.gap'
                                          : 'explain.unadjusted.gap',
                                        {
                                          gap: t(`${moduleConfig?.module}_gap`),
                                          unit: t(
                                            `dependentCharacteristic.name.${latestGap?.dependentCharacteristic.name}` as any,
                                          ),
                                        },
                                      )}
                                    </Typography>
                                  }
                                >
                                  <Stack className="mr-4 flex-row">
                                    <Typography className="w-fit mx-2">
                                      {t('unadjusted')}
                                    </Typography>
                                    <HelpOutlineRounded
                                      fontSize="small"
                                      sx={{ color: theme.palette.text.secondary }}
                                    />
                                  </Stack>
                                </Tooltip>
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <Stack className="flex-row items-end justify-center">
                                  <Typography variant="h3">
                                    {`${Number(unadjusted).toLocaleString(currentLanguage, {
                                      maximumFractionDigits: 2,
                                      minimumFractionDigits: 2,
                                    })} %`}
                                  </Typography>
                                </Stack>
                              </Grid>
                            </Grid>
                            <Grid
                              container
                              spacing={1}
                              mt={4}
                              display="flex"
                              justifyContent="flex-start"
                              alignItems="flex-end"
                            >
                              <Grid size={{ xs: 6 }} className="flex pl-2 pt-2">
                                <Tooltip
                                  placement="right"
                                  arrow
                                  enterTouchDelay={0}
                                  title={
                                    <Typography>
                                      {t('explain.adjusted.gap', {
                                        gap: t(`${moduleConfig?.module}_gap`),
                                      })}
                                    </Typography>
                                  }
                                >
                                  <Stack className="mr-4 flex-row">
                                    <Typography className="w-fit mx-2">{t('adjusted')}</Typography>
                                    <HelpOutlineRounded
                                      fontSize="small"
                                      sx={{ color: theme.palette.text.secondary }}
                                    />
                                  </Stack>
                                </Tooltip>
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <Stack className="flex-row items-end justify-center">
                                  <Typography>{t('cant.calculate')}</Typography>
                                </Stack>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6, lg: 6.5 }} className="flex">
                    <Card
                      elevation={1}
                      sx={{
                        boxShadow: isCauseHovered
                          ? `2px 2px 8px 2px ${moduleConfig.fsColor.primary}`
                          : `2px 2px 8px 2px ${moduleConfig.fsColor.primary}66`,
                        // border: `1px solid ${moduleConfig.fsColor.primary}66`,
                        borderRadius: 3,
                      }}
                      className="w-full causes_walkthrough"
                      onMouseEnter={() => setIsCauseHovered(true)}
                      onMouseLeave={() => setIsCauseHovered(false)}
                    >
                      <CardContent className="h-full">
                        <Stack className="justify-between h-full">
                          <Box>
                            <Typography variant="h2" className="font-semibold" mt={-0.5}>
                              {t('gap_causes')}
                            </Typography>

                            {latestGap?.adjusted !== null ? (
                              <Stack className="p-4 flex flex-row items-center justify-center mt-4">
                                <Tooltip
                                  placement="top"
                                  arrow
                                  enterTouchDelay={0}
                                  title={
                                    <Typography>
                                      {t('explain.gap.causes', {
                                        gap: t(`${moduleConfig?.module}_gap`),
                                        number: Number(explained).toLocaleString(currentLanguage, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        }),
                                      })}
                                    </Typography>
                                  }
                                >
                                  <Box
                                    id="hovered-class-2"
                                    className="text-center py-1 px-2 rounded-xl min-w-[150px] max-w-[190px]"
                                    sx={{
                                      backgroundColor: isCauseHovered
                                        ? `${diversityDimenstionConfig?.fsColor.primary}33`
                                        : '',
                                      outline: isCauseHovered ? `1px solid` : '',
                                      outlineColor: isCauseHovered
                                        ? `${diversityDimenstionConfig?.fsColor.primary}90`
                                        : '',
                                    }}
                                  >
                                    <Stack className="ml-2">
                                      <Stack className="text-start flex-row">
                                        <Typography variant="n2" mr={1}>{`${Number(
                                          explained,
                                        ).toLocaleString(currentLanguage, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })} %`}</Typography>
                                        <HelpOutlineRounded
                                          fontSize="small"
                                          sx={{ color: theme.palette.text.secondary }}
                                        />
                                      </Stack>
                                      <Typography className="text-start">
                                        {t('gap_causes')}
                                      </Typography>
                                    </Stack>
                                  </Box>
                                </Tooltip>

                                <Box
                                  width={40}
                                  height={2}
                                  sx={{ backgroundColor: theme.palette.text.secondary }}
                                />
                                <Box
                                  sx={{
                                    width: '6px',
                                    height: totalHeight + 32,
                                    border: `2px solid ${theme.palette.text.secondary}`,
                                    borderRight: 0,
                                    borderRadius: 1,
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                  }}
                                />
                                <Box className="flex flex-col md:flex-row items-center justify-center p-4 pr-0">
                                  <Box
                                    className={`relative h-[${totalHeight + 32}px] w-6 rounded-lg`}
                                  >
                                    <AnimatedProgressBar
                                      progress={posGapHeight}
                                      color={diversityDimenstionConfig?.fsColor.light}
                                      orientation="vertical"
                                    />

                                    <Box className="w-4 rounded-full h-4 my-2 mx-1 flex items-center justify-center">
                                      <AddRounded sx={{ fontSize: 16 }} />
                                    </Box>
                                    <AnimatedProgressBar
                                      progress={negGapHeight}
                                      color={diversityDimenstionConfig?.fsColor.colors[2]}
                                      orientation="vertical"
                                    />
                                  </Box>
                                </Box>
                                <Stack>
                                  <Stack height={posGapHeight} className="justify-center mb-8">
                                    <Stack className="flex-row items-center">
                                      <Box
                                        width={40}
                                        height={2}
                                        sx={{
                                          backgroundColor: diversityDimenstionConfig?.fsColor.light,
                                        }}
                                      />
                                      <Tooltip
                                        placement="top"
                                        arrow
                                        enterTouchDelay={0}
                                        title={
                                          <Typography>
                                            {t('explain.positive.causes', {
                                              gap: t(`${moduleConfig?.module}_gap`),
                                              number: Number(posGapValue).toLocaleString(
                                                currentLanguage,
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                },
                                              ),
                                            })}
                                          </Typography>
                                        }
                                      >
                                        <Stack className="ml-4 min-w-[135px] max-w-[190px]">
                                          <Stack className="text-start flex-row ">
                                            <Typography variant="n2" mr={1}>{`${Number(
                                              posGapValue,
                                            ).toLocaleString(currentLanguage, {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })} %`}</Typography>
                                            <HelpOutlineRounded
                                              fontSize="small"
                                              sx={{ color: theme.palette.text.secondary }}
                                            />
                                          </Stack>
                                          <Typography className="text-start">
                                            Positive {t('gap_causes')}
                                          </Typography>
                                        </Stack>
                                      </Tooltip>
                                    </Stack>
                                  </Stack>
                                  <Stack height={negGapHeight} className="justify-center">
                                    <Stack className="flex-row items-center">
                                      <Box
                                        width={40}
                                        height={2}
                                        sx={{
                                          backgroundColor:
                                            diversityDimenstionConfig?.fsColor.colors[2],
                                        }}
                                      />
                                      <Tooltip
                                        placement="bottom"
                                        arrow
                                        enterTouchDelay={0}
                                        title={
                                          <Typography>
                                            {t('explain.negative.causes', {
                                              gap: t(`${moduleConfig?.module}_gap`),
                                              number: Number(neGapValue).toLocaleString(
                                                currentLanguage,
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                },
                                              ),
                                            })}
                                          </Typography>
                                        }
                                      >
                                        <Stack className="ml-4 min-w-[135px] max-w-[190px]">
                                          <Stack className="text-start flex-row">
                                            <Typography variant="n2" mr={1}>{`${Number(
                                              neGapValue,
                                            ).toLocaleString(currentLanguage, {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })} %`}</Typography>
                                            <HelpOutlineRounded
                                              fontSize="small"
                                              sx={{ color: theme.palette.text.secondary }}
                                            />
                                          </Stack>
                                          <Typography className="text-start">
                                            Negative {t('gap_causes')}
                                          </Typography>
                                        </Stack>
                                      </Tooltip>
                                    </Stack>
                                  </Stack>
                                </Stack>
                              </Stack>
                            ) : (
                              <Box>
                                <Typography className="text-center mt-10 mx-8">
                                  {t('gap.causes.empty')}
                                </Typography>
                                <Typography className="text-center mt-4 mx-8">
                                  {t('gap.causes.empty.card')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Stack className="flex flex-row items-center justify-center mt-4">
                            <Button
                              variant="contained"
                              startIcon={<AlignHorizontalCenterRounded />}
                              onClick={() => {
                                if (walkthroughStatus === WALKTHROUGH_STATUS.InProgress) {
                                  dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Paused))
                                }
                                navigate(`/fs/causes/${contextModelVariantId}`)
                              }}
                            >
                              {t('open.detailed.view')}
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6, lg: 5.5 }} className="flex">
                    <Card
                      elevation={1}
                      sx={{
                        boxShadow: `2px 2px 8px 2px ${moduleConfig.fsColor.primary}66`,
                        // border: `1px solid ${moduleConfig.fsColor.primary}66`,
                        borderRadius: 3,
                      }}
                      className="w-full gap_1_walkthrough"
                    >
                      <CardContent className="h-full">
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          mt={-0.75}
                        >
                          <Typography variant="h2" className="font-semibold">{`${t(
                            'gap_history',
                          )}`}</Typography>
                          <Tooltip
                            title={
                              <Box p={1} px={2}>
                                <Typography>{t('timeline.tooltip')}</Typography>
                              </Box>
                            }
                            placement="bottom"
                            arrow
                            enterTouchDelay={0}
                          >
                            <InfoOutlineRounded
                              fontSize="small"
                              sx={{ ml: 1, color: theme.palette.text.secondary }}
                            />
                          </Tooltip>
                        </Stack>
                        <Stack className="justify-between h-full pb-6">
                          <Box>
                            <Box className="w-full items-center justify-center mt-10">
                              <Stack
                                direction="row"
                                alignItems="center"
                                my={1}
                                justifyContent="center"
                              >
                                <Typography variant="h3">
                                  {`${t('unadjusteder')} ${t('gap')}: ${
                                    unadjDiff !== 0
                                      ? `${t('from')} ${Number(
                                          sortedGaps[prevIndex].unadjusted,
                                        ).toLocaleString(currentLanguage, {
                                          maximumFractionDigits: 2,
                                          minimumFractionDigits: 2,
                                        })} % ${t('to')} `
                                      : ''
                                  }${Number(unadjusted).toLocaleString(currentLanguage, {
                                    maximumFractionDigits: 2,
                                    minimumFractionDigits: 2,
                                  })} %`}
                                </Typography>
                              </Stack>
                              {(sortedGaps.length ?? 0) > 1 && (
                                <Stack alignItems="center">
                                  <Stack width="65%">
                                    <Typography variant="body2" className="text-center">
                                      {unadjDiff > 0 ? (
                                        <ArrowUpwardRounded
                                          sx={{ fontSize: 16, mb: '-4px' }}
                                          color="error"
                                        />
                                      ) : unadjDiff < 0 ? (
                                        <ArrowDownwardRounded
                                          sx={{ fontSize: 16, mb: '-4px' }}
                                          color="success"
                                        />
                                      ) : null}
                                      {unadjDiff !== 0
                                        ? ` ${unadjDiff > 0 ? '+' : '-'}${Math.abs(
                                            unadjDiff,
                                          ).toLocaleString(currentLanguage, {
                                            maximumFractionDigits: 2,
                                          })} % ${t('compare.to.prev.data')}`
                                        : t('no.compare.to.prev.data')}
                                    </Typography>
                                  </Stack>
                                </Stack>
                              )}
                            </Box>
                            {latestGap?.adjusted !== null && (
                              <Box className="w-full items-center justify-center mt-6">
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  my={1}
                                  justifyContent="center"
                                >
                                  <Typography variant="h3">
                                    {`${t('adjusteder')} ${t('gap')}: ${
                                      adjDiff !== 0
                                        ? `${t('from')} ${Number(
                                            sortedGaps[prevIndex].adjusted,
                                          ).toLocaleString(currentLanguage, {
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 2,
                                          })} % ${t('to')} `
                                        : ''
                                    }${Number(adjusted).toLocaleString(currentLanguage, {
                                      maximumFractionDigits: 2,
                                      minimumFractionDigits: 2,
                                    })} %`}
                                  </Typography>
                                </Stack>
                                {(sortedGaps.length ?? 0) > 1 && (
                                  <Stack alignItems="center">
                                    <Stack width="65%">
                                      <Typography variant="body2" className="text-center">
                                        {adjDiff > 0 ? (
                                          <ArrowUpwardRounded
                                            sx={{ fontSize: 16, mb: '-4px' }}
                                            color="error"
                                          />
                                        ) : adjDiff < 0 ? (
                                          <ArrowDownwardRounded
                                            sx={{ fontSize: 16, mb: '-4px' }}
                                            color="success"
                                          />
                                        ) : null}
                                        {adjDiff !== 0
                                          ? ` ${adjDiff > 0 ? '+' : '-'}${Math.abs(
                                              adjDiff,
                                            ).toLocaleString(currentLanguage, {
                                              maximumFractionDigits: 2,
                                            })} % ${t('compare.to.prev.data')}`
                                          : t('no.compare.to.prev.data')}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                )}
                              </Box>
                            )}
                          </Box>
                          <Box className="flex flex-row items-center justify-center">
                            <Button
                              variant="contained"
                              onClick={() => {
                                if (walkthroughStatus === WALKTHROUGH_STATUS.InProgress) {
                                  dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Paused))
                                }
                                navigate(`/fs/timeline/${contextModelVariantId}`)
                              }}
                              startIcon={<TimelineRounded />}
                            >
                              {t('open.detailed.view')}
                            </Button>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6, lg: 6.5 }} className="flex">
                    <Card
                      elevation={1}
                      sx={{
                        boxShadow: `2px 2px 8px 2px ${moduleConfig.fsColor.primary}66`,
                        // border: `1px solid ${moduleConfig.fsColor.primary}66`,
                        borderRadius: 3,
                      }}
                      className="w-full gap_2_walkthrough"
                    >
                      <CardContent className="h-full">
                        {latestGap && (
                          <FsOptionsForActions
                            latestGap={latestGap}
                            contextModelVariantId={contextModelVariantId}
                            moduleConfig={moduleConfig}
                            diversityDimenstionConfig={diversityDimenstionConfig}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            <AnimatePresence>
              {isCauseHovered && lineProps && (
                <motion.svg
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 9999,
                  }}
                >
                  <motion.path
                    d={lineProps}
                    fill="none"
                    stroke={diversityDimenstionConfig?.fsColor.primary}
                    strokeWidth={1}
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.5 }}
                  />
                </motion.svg>
              )}
            </AnimatePresence>
            <FsLeftInfoDrawer open={helpOpen} onClose={handleHelpClose}>
              <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
                {helpTextHtml}
              </Typography>
            </FsLeftInfoDrawer>
          </>
        )}
      {!(
        contextModels &&
        id &&
        moduleConfig &&
        diversityDimenstionConfig &&
        contextVariantGaps &&
        contextVariantGaps[contextModelVariantId]
      ) && <FsFullPageLoader />}
    </>
  )
}

export default FairView
